import React, { useState, useEffect } from 'react';
import { risksAPI } from '../lib/api';
import { useNotification } from '../contexts/NotificationContext';
import {
  AlertTriangle,
  Plus,
  Loader2,
  Search,
  X,
  Trash2,
  Edit3,
  Target,
  TrendingUp
} from 'lucide-react';

const CATEGORIES = [
  { value: 'operational', label: 'Operational', color: 'bg-blue-500' },
  { value: 'technical', label: 'Technical', color: 'bg-purple-500' },
  { value: 'compliance', label: 'Compliance', color: 'bg-green-500' },
  { value: 'financial', label: 'Financial', color: 'bg-yellow-500' },
  { value: 'reputational', label: 'Reputational', color: 'bg-red-500' }
];

const RiskRegister = () => {
  const { notify } = useNotification();
  const [risks, setRisks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [filter, setFilter] = useState({ category: 'all', search: '' });
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', category: 'operational', likelihood: 3, impact: 3,
    owner: '', mitigationPlan: '', mitigationStatus: 'not_started', targetDate: ''
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [risksRes, statsRes] = await Promise.all([risksAPI.getAll(), risksAPI.getStats()]);
      setRisks(risksRes.data || []);
      setStats(statsRes.data);
    } catch (err) { notify.error('Failed to load risks'); }
    finally { setLoading(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (selectedRisk) {
        await risksAPI.update(selectedRisk.id, form);
        notify.success('Risk updated');
      } else {
        await risksAPI.create(form);
        notify.success('Risk created');
      }
      setShowModal(false); setSelectedRisk(null);
      setForm({ title: '', description: '', category: 'operational', likelihood: 3, impact: 3, owner: '', mitigationPlan: '', mitigationStatus: 'not_started', targetDate: '' });
      fetchData();
    } catch (err) { notify.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (riskId) => {
    if (!window.confirm('Delete this risk?')) return;
    try {
      await risksAPI.delete(riskId);
      notify.success('Risk deleted');
      fetchData();
    } catch (err) { notify.error('Failed to delete'); }
  };

  const getRiskLevel = (score) => {
    if (score >= 15) return { label: 'Critical', color: 'bg-red-500', text: 'text-red-400' };
    if (score >= 10) return { label: 'High', color: 'bg-orange-500', text: 'text-orange-400' };
    if (score >= 5) return { label: 'Medium', color: 'bg-yellow-500', text: 'text-yellow-400' };
    return { label: 'Low', color: 'bg-green-500', text: 'text-green-400' };
  };

  const filteredRisks = risks.filter(r => {
    if (filter.category !== 'all' && r.category !== filter.category) return false;
    if (filter.search && !r.title.toLowerCase().includes(filter.search.toLowerCase())) return false;
    return true;
  });

  if (loading) return <div className="min-h-screen bg-slate-900 flex items-center justify-center"><Loader2 className="w-10 h-10 text-violet-500 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-16">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Target className="w-7 h-7 text-orange-400" />Risk Register</h1>
            <p className="text-slate-400 mt-1">Identify, assess, and mitigate organizational risks</p>
          </div>
          <button onClick={() => { setSelectedRisk(null); setForm({ title: '', description: '', category: 'operational', likelihood: 3, impact: 3, owner: '', mitigationPlan: '', mitigationStatus: 'not_started', targetDate: '' }); setShowModal(true); }} className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg"><Plus className="w-4 h-4" />Add Risk</button>
        </div>

        {/* Stats & Heat Map */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Stats */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4">Risk Summary</h3>
            <div className="grid grid-cols-3 gap-4">
              <div><div className="text-2xl font-bold text-white">{stats?.total || 0}</div><div className="text-sm text-slate-400">Total Risks</div></div>
              <div><div className="text-2xl font-bold text-red-400">{stats?.highRisks || 0}</div><div className="text-sm text-slate-400">High/Critical</div></div>
              <div><div className="text-2xl font-bold text-green-400">{stats?.byStatus?.closed || 0}</div><div className="text-sm text-slate-400">Closed</div></div>
            </div>
          </div>

          {/* Heat Map */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4">Risk Heat Map</h3>
            <div className="grid grid-cols-6 gap-1">
              <div></div>
              {[1,2,3,4,5].map(i => <div key={i} className="text-center text-xs text-slate-500">{i}</div>)}
              {[5,4,3,2,1].map((l, lIdx) => (
                <React.Fragment key={l}>
                  <div className="text-xs text-slate-500 flex items-center">{l}</div>
                  {[0,1,2,3,4].map(iIdx => {
                    const count = stats?.heatMap?.[4-lIdx]?.[iIdx] || 0;
                    const score = l * (iIdx + 1);
                    let bg = 'bg-green-500/20';
                    if (score >= 15) bg = 'bg-red-500/40';
                    else if (score >= 10) bg = 'bg-orange-500/30';
                    else if (score >= 5) bg = 'bg-yellow-500/20';
                    return (
                      <div key={iIdx} className={`h-8 rounded flex items-center justify-center text-xs text-white ${bg}`}>
                        {count > 0 ? count : ''}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
              <div></div>
              <div className="col-span-5 text-center text-xs text-slate-500 mt-1">Impact →</div>
            </div>
            <div className="text-xs text-slate-500 -rotate-90 absolute left-0 top-1/2">Likelihood ↑</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" placeholder="Search risks..." value={filter.search} onChange={(e) => setFilter({ ...filter, search: e.target.value })} className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white" />
          </div>
          <select value={filter.category} onChange={(e) => setFilter({ ...filter, category: e.target.value })} className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white">
            <option value="all">All Categories</option>
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>

        {/* Risks List */}
        {filteredRisks.length === 0 ? (
          <div className="bg-slate-800/50 rounded-xl p-12 border border-slate-700 text-center">
            <Target className="w-16 h-16 text-slate-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Risks Found</h3>
            <p className="text-slate-400">Add risks to your register to track them</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRisks.map(risk => {
              const riskLevel = getRiskLevel(risk.riskScore);
              const catConfig = CATEGORIES.find(c => c.value === risk.category) || CATEGORIES[0];

              return (
                <div key={risk.id} className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 hover:border-slate-600 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-white">{risk.title}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs text-white ${catConfig.color}`}>{catConfig.label}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${riskLevel.color}/20 ${riskLevel.text}`}>{riskLevel.label} ({risk.riskScore})</span>
                      </div>
                      <p className="text-slate-400 text-sm mb-3">{risk.description}</p>
                      <div className="flex items-center gap-6 text-sm">
                        <div><span className="text-slate-500">Likelihood:</span> <span className="text-white">{risk.likelihood}/5</span></div>
                        <div><span className="text-slate-500">Impact:</span> <span className="text-white">{risk.impact}/5</span></div>
                        {risk.owner && <div><span className="text-slate-500">Owner:</span> <span className="text-white">{risk.owner}</span></div>}
                        <div><span className="text-slate-500">Mitigation:</span> <span className={risk.mitigationStatus === 'completed' ? 'text-green-400' : risk.mitigationStatus === 'in_progress' ? 'text-yellow-400' : 'text-slate-400'}>{risk.mitigationStatus?.replace('_', ' ')}</span></div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => { setSelectedRisk(risk); setForm(risk); setShowModal(true); }} className="p-2 text-slate-400 hover:text-violet-400"><Edit3 className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(risk.id)} className="p-2 text-slate-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl w-full max-w-lg border border-slate-700 shadow-xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-slate-700 flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white">{selectedRisk ? 'Edit Risk' : 'Add Risk'}</h3>
                <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleSave} className="p-6 space-y-4">
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Title *</label>
                  <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" required />
                </div>
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Description</label>
                  <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white resize-none" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Category</label>
                    <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white">
                      {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Owner</label>
                    <input type="text" value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Likelihood (1-5)</label>
                    <input type="range" min="1" max="5" value={form.likelihood} onChange={(e) => setForm({ ...form, likelihood: parseInt(e.target.value) })} className="w-full" />
                    <div className="text-center text-white">{form.likelihood}</div>
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Impact (1-5)</label>
                    <input type="range" min="1" max="5" value={form.impact} onChange={(e) => setForm({ ...form, impact: parseInt(e.target.value) })} className="w-full" />
                    <div className="text-center text-white">{form.impact}</div>
                  </div>
                </div>
                <div className="text-center p-3 rounded-lg" style={{ backgroundColor: getRiskLevel(form.likelihood * form.impact).color.replace('bg-', 'rgba(').replace('-500', ', 0.2)') }}>
                  <span className="text-white">Risk Score: <strong>{form.likelihood * form.impact}</strong> ({getRiskLevel(form.likelihood * form.impact).label})</span>
                </div>
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Mitigation Plan</label>
                  <textarea value={form.mitigationPlan} onChange={(e) => setForm({ ...form, mitigationPlan: e.target.value })} rows={2} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white resize-none" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Mitigation Status</label>
                    <select value={form.mitigationStatus} onChange={(e) => setForm({ ...form, mitigationStatus: e.target.value })} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white">
                      <option value="not_started">Not Started</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Target Date</label>
                    <input type="date" value={form.targetDate} onChange={(e) => setForm({ ...form, targetDate: e.target.value })} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 rounded-lg">Cancel</button>
                  <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg disabled:opacity-50">{saving ? 'Saving...' : 'Save Risk'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default RiskRegister;
